import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import { guardNumber } from "@msuite/utils/numbers";
import type { SubstitutionPlanFrontendFormSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { SubstitutionPlanContactModal } from "@/modals/users/substitution-plan-contact-modal";
import { useSubstitutionPlanFormContext } from "./context";

/** Props Interface */
interface SubstitutionPlanNotifyContactsSelectProps {
	itemId: string;
	projectId: string;
}

export function SubstitutionPlanNotifyContactsSelect({
	projectId,
	itemId,
}: SubstitutionPlanNotifyContactsSelectProps) {
	/** Hooks */
	const disclosure = useDisclosure();
	const { t } = useTranslation();
	const { getItemIndex } = useSubstitutionPlanFormContext();
	const methods = useFormContext<SubstitutionPlanFrontendFormSchema>();
	const index = getItemIndex(itemId);
	const [contactNotifications] = useWatch({
		control: methods.control,
		name: [`items.${index}.contactNotifications`, `items.${index}.isAssignmentNotRequired`],
	});

	/** Computed */
	const currentlyAssignedcontactNotificationsLength = guardNumber(contactNotifications?.length);
	const hasContacts = currentlyAssignedcontactNotificationsLength > 0;

	/** Render */
	return (
		<React.Fragment>
			<div
				className="h-stack cursor-pointer *:tabular-nums shrink-0"
				onClick={disclosure.onOpen}
			>
				<Icon
					name="User"
					className={cn(hasContacts ? "stroke-accent-500" : "stroke-primary-500")}
				/>
				<p>
					{t("substitutions.n_contacts_will_be_notified", {
						count: `${currentlyAssignedcontactNotificationsLength}`,
					})}
				</p>
			</div>
			{disclosure.isOpen && (
				<SubstitutionPlanContactModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					projectId={projectId}
					defaultValues={methods.getValues(`items.${index}.contactNotifications`)}
					substitutionPlanProjectItemId={itemId}
					onComplete={(items) => {
						methods.setValue(`items.${index}.contactNotifications`, items.items);
					}}
				/>
			)}
		</React.Fragment>
	);
}
